import React, { useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-luxon";
import { DateTime } from "luxon";
import Modal from "react-bootstrap/Modal";
import DateLabels from "./DateLabels";
import { getColours, getLightColour } from "./Util";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  DateLabels
);

var hiddenNuclear = true;
var hiddenGeothermal = true;
var hiddenBiomass = true;
var hiddenCoal = true;
var hiddenWind = true;
var hiddenSolar = true;
var hiddenHydro = true;
var hiddenGas = true;
var hiddenOil = true;
var hiddenUnknown = true;
var hiddenHydroDischarge = true;
var hiddenBatteryDischarge = true;

export default function SourceModal(props) {
  useEffect(
    () => {
      for (let i = 0; i < props.sourceNuclear.length; i++) {
        if (props.sourceNuclear[i].y > 0) {
          hiddenNuclear = false;
        }
        if (props.sourceGeothermal[i].y > 0) {
          hiddenGeothermal = false;
        }
        if (props.sourceBiomass[i].y > 0) {
          hiddenBiomass = false;
        }
        if (props.sourceCoal[i].y > 0) {
          hiddenCoal = false;
        }
        if (props.sourceWind[i].y > 0) {
          hiddenWind = false;
        }
        if (props.sourceSolar[i].y > 0) {
          hiddenSolar = false;
        }
        if (props.sourceHydro[i].y > 0) {
          hiddenHydro = false;
        }
        if (props.sourceGas[i].y > 0) {
          hiddenGas = false;
        }
        if (props.sourceOil[i].y > 0) {
          hiddenOil = false;
        }
        if (props.sourceUnknown[i].y > 0) {
          hiddenUnknown = false;
        }
        if (props.sourceHydroDischarge[i].y > 0) {
          hiddenHydroDischarge = false;
        }
        if (props.sourceBatteryDischarge[i].y > 0) {
          hiddenBatteryDischarge = false;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props]
  );

  function getDatasets() {
    let datasets = [];

    if (!hiddenNuclear) {
      datasets.push({
        label: "Nuclear",
        data: props.sourceNuclear,
        borderColor: getColours()[0],
        pointRadius: 0,
        backgroundColor: getColours()[0],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 11,
      });
    }

    if (!hiddenGeothermal) {
      datasets.push({
        label: "Geothermal",
        data: props.sourceGeothermal,
        borderColor: getColours()[1],
        pointRadius: 0,
        backgroundColor: getColours()[1],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 10,
      });
    }

    if (!hiddenBiomass) {
      datasets.push({
        label: "Biomass",
        data: props.sourceBiomass,
        borderColor: getColours()[2],
        pointRadius: 0,
        backgroundColor: getColours()[2],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 9,
      });
    }

    if (!hiddenCoal) {
      datasets.push({
        label: "Coal",
        data: props.sourceCoal,
        borderColor: getColours()[3],
        pointRadius: 0,
        backgroundColor: getColours()[3],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 8,
      });
    }

    if (!hiddenWind) {
      datasets.push({
        label: "Wind",
        data: props.sourceWind,
        borderColor: getColours()[4],
        pointRadius: 0,
        backgroundColor: getColours()[4],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 7,
      });
    }

    if (!hiddenSolar) {
      datasets.push({
        label: "Solar",
        data: props.sourceSolar,
        borderColor: getColours()[5],
        pointRadius: 0,
        backgroundColor: getColours()[5],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 6,
      });
    }

    if (!hiddenHydro) {
      datasets.push({
        label: "Hydro",
        data: props.sourceHydro,
        borderColor: getColours()[6],
        pointRadius: 0,
        backgroundColor: getColours()[6],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 5,
      });
    }

    if (!hiddenGas) {
      datasets.push({
        label: "Gas",
        data: props.sourceGas,
        borderColor: getColours()[7],
        pointRadius: 0,
        backgroundColor: getColours()[7],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 4,
      });
    }

    if (!hiddenOil) {
      datasets.push({
        label: "Oil",
        data: props.sourceOil,
        borderColor: getColours()[8],
        pointRadius: 0,
        backgroundColor: getColours()[8],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 3,
      });
    }

    if (!hiddenUnknown) {
      datasets.push({
        label: "Unknown",
        data: props.sourceUnknown,
        borderColor: getColours()[9],
        pointRadius: 0,
        backgroundColor: getColours()[9],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 2,
      });
    }

    if (!hiddenHydroDischarge) {
      datasets.push({
        label: "Hydro Storage",
        data: props.sourceHydroDischarge,
        borderColor: getColours()[10],
        pointRadius: 0,
        backgroundColor: getColours()[10],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 1,
        hidden: hiddenHydroDischarge,
      });
    }

    if (!hiddenBatteryDischarge) {
      datasets.push({
        label: "Battery Storage",
        data: props.sourceBatteryDischarge,
        borderColor: getColours()[11],
        pointRadius: 0,
        backgroundColor: getColours()[11],
        cubicInterpolationMode: "monotone",
        fill: true,
        order: 0,
        hidden: hiddenBatteryDischarge,
      });
    }

    return datasets;
  }

  const graphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: "index",
        itemSort: (a, b) => {
          return a.dataSetIndex > b.dataSetIndex ? 1 : -1;
        },
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label = label + ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(3) + " GW";
            }
            return label;
          },
          footer: function (tooltipItems) {
            let sum = 0;
            tooltipItems.forEach(function (tooltipItem) {
              sum += tooltipItem.parsed.y;
            });
            return "Total Production: " + sum.toFixed(3) + " GW";
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    layout: {
      padding: {
        bottom: 20,
      },
    },
    scales: {
      x: {
        offset: false,
        type: "time",
        adapters: {
          date: {
            zone: "Canada/Mountain",
          },
        },
        time: {
          unit: "day",
          displayFormats: {
            hour: DateTime.DATE_SHORT,
          },
          tooltipFormat: DateTime.DATETIME_FULL,
        },
        grid: {
          offset: false,
        },
        ticks: {
          callback: (label) => "",
        },
      },
      y: {
        ticks: {
          callback: (label) => `${label} GW`,
        },
        stacked: true,
      },
    },
  };

  let graphData = {
    datasets: getDatasets(),
  };

  return (
    <Modal
      show={props.show}
      onHide={props.close}
      dialogClassName="historyModal"
      centered
    >
      <Modal.Header style={{ backgroundColor: getLightColour() }} closeButton>
        <Modal.Title>Electricity Production History (GW)</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: props.height + "px" }}>
        <Line options={graphOptions} data={graphData} />
      </Modal.Body>
    </Modal>
  );
}
